import type {
  AggregateConstraintValidation,
  Allocation,
  CashLot as Cash,
  CashflowTarget,
  DetailedProposal,
  ObjectiveStrategy,
  ProposalOrder,
  TaxProfile as TaxProfileValue,
} from '@moment/horizon-data-access/proposal'
import type { FilterCondition } from '@moment/trading-types/filter-expression'
import type { Currency } from '@moment/trading-types/reference-data'
import type { FilterListConstraintType } from '@/portfolio/proposal-builder/components/filter-list-risk-factor/utils'
import type { ComplianceMap } from '@/portfolio/proposal-builder/components/risk-factor-list/types'
import type { EquationToken } from '@/portfolio/proposal-builder/objective-function/equation-tokens'
import type {
  CashflowBandConstraint,
  LadderPolicyConstraint,
  ModelConstraint,
  UniverseConstraint,
} from '@/types/api/constraints'
import type { Holding } from '@/types/api/risk-factor'

import {
  ProposalStatus,
  UniverseConstraintType,
} from '@moment/horizon-data-access/proposal'
import { Operator } from '@moment/trading-types/filter'
import { create } from 'zustand'
import { devtools } from 'zustand/middleware'
import { immer } from 'zustand/middleware/immer'

import { globalLogger } from '@/lib/logger'
import {
  calculateAvailableAccountCash,
  distributeCashProportionally,
} from '@/portfolio/helpers/calculate-available-account-cash'
import { InvestorInfoField } from '@/portfolio/proposal-builder/config'
import { allocationNetMoney } from '@/portfolio/proposal-builder/data/helpers/allocation-net-money'
import {
  applyCashflowBand,
  applyCashflowBandPriority,
  hasRecurringCashflowTarget,
  readCashflowBand,
} from '@/portfolio/proposal-builder/data/helpers/cashflow-band-constraint'
import { eachConstraintInFactor } from '@/portfolio/proposal-builder/data/helpers/each-constraint-in-factor'
import {
  applyMaxSells,
  readMaxSells,
} from '@/portfolio/proposal-builder/data/helpers/max-sells-constraint'
import { applyMinimumQuantity } from '@/portfolio/proposal-builder/data/helpers/minimum-quantity-constraint'
import { findAllocationInOrders } from '@/portfolio/proposal-builder/data/helpers/order-manager'
import {
  DEFAULT_CASHFLOW_TARGET_PRIORITY,
  parsePriority,
} from '@/portfolio/proposal-builder/data/helpers/parse-priority'
import { removeConstraintFromTree } from '@/portfolio/proposal-builder/data/helpers/remove-constraint-from-tree'
import {
  getSleeveAggregateValidations,
  getSleeveComplianceMap,
} from '@/portfolio/proposal-builder/data/helpers/sleeve-compliance'
import {
  createEmptyFieldConstraints,
  createRiskFactor,
  createSleeve,
  DEFAULT_SLEEVE_ID,
  generateId,
} from '@/portfolio/proposal-builder/data/helpers/sleeve-manager'
import { placeConstraintInTree } from '@/portfolio/proposal-builder/data/transformers/constraints-to-risk-factors'
import {
  placeDiversificationParameterConstraint,
  removeDiversificationParameterConstraintById,
} from '@/portfolio/proposal-builder/data/transformers/diversification-parameter-constraints'
import {
  buildGlobalUniverseConstraint,
  createDefaultGlobalConstraints,
  getSleeveCurrency,
  readCurrencyConstraint,
} from '@/portfolio/proposal-builder/data/transformers/global-constraints'
import {
  computeInitialHoldings,
  extractAccountStatistics,
  extractSleeveCompliance,
  extractSleeveStatistics,
} from '@/portfolio/proposal-builder/data/transformers/proposal-api-to-state'
import { computeDisabledRiskFactors } from '@/portfolio/proposal-builder/data/transformers/risk-factors-to-constraints'
import { CURRENCY_SYMBOLS } from '@/portfolio/proposal-builder/formatting-constants'
import {
  calculateSleeveCash,
  calculateTotalAccountCash,
} from '@/portfolio/proposal-builder/helpers/calculate-total-account-cash'
import {
  type AccountID,
  ConfigKey,
  DEFAULT_INVESTOR_INFO,
  type DiversificationParameterConstraintKey,
  type DiversificationParameterConstraints,
  Field,
  type FieldConstraints,
  type FilterListConstraints,
  type InvestorInfo,
  Priority,
  type ProposalBuilderState,
  type ProposalBuilderView,
  type ProposalMode,
  type RiskFactor,
  type Sleeve,
  type SleeveCompliance,
  type SleeveID,
  type SleeveStatistics,
} from '@/portfolio/proposal-builder/types'
import { OrderSide } from '@/types/enums/order'
import { RebalanceOptions } from '@/types/enums/sleeve-management'
import { isCashInstrumentId } from '@/utils/instruments'
import { createSelectors } from '@/utils/zustand/create-selectors'

// Initial state with one default sleeve
const createInitialState = (): ProposalBuilderState => {
  return {
    sleeves: [createSleeve(DEFAULT_SLEEVE_ID)],
    orders: [],
    proposalId: '',
    proposalName: '',
    isAccountLinked: false,
    optimizationId: null,
    version: 0,
    proposalStatus: ProposalStatus.in_review,
    activeAccount: '',
    accountBefore: null,
    accountAfter: null,
    holdingsHeld: {},
    holdingsBefore: {},
    holdingsAfter: {},
    proposalActions: [],
    sleeveStatistics: {},
    accountStatistics: {
      before: null,
      after: null,
    },
    sleeveCompliance: {
      [DEFAULT_SLEEVE_ID]: {
        before: [],
        after: [],
      },
    },
    constraintRelaxation: {},
    metadata: { flags: {} },
    flags: {
      dirtyCount: 0,
    },
  }
}

// Safety net for writes that land before hydration, or for keys outside the configured set.
function getOrCreateRiskFactor(
  sleeve: Sleeve,
  configKey: ConfigKey,
): RiskFactor {
  let riskFactor = sleeve.state.riskFactors[configKey]
  if (!riskFactor) {
    riskFactor = createRiskFactor(configKey)
    sleeve.state.riskFactors[configKey] = riskFactor
  }
  return riskFactor
}

const createInitialCashObject = (): Cash => {
  return {
    currency: 'USD',
    moment_lot_id: '',
    custodian_lot_id: '',
    moment_reserved_quantity: 0,
    open_quantity: 0,
    unsettled_open_quantity: 0,
    unsettled_closed_quantity: 0,
    settled_open_quantity: 0,
    settled_closed_quantity: 0,
  }
}

function cashBelongsToSleeve(cash: Cash, sleeveId: SleeveID): boolean {
  return cash.sleeve_id === sleeveId
}

// The one currency write path: rewrites the target sleeves' currency
// constraints and the in-scope cash lots (a lot with no `sleeve_id` predates
// per-sleeve cash and still stands for the account). Seeding a first cash lot
// is setInvestorField's concern, not a rule placement's.
function writeSleeveCurrency(
  draft: ProposalBuilderState,
  currency: Currency,
  targets: Sleeve[],
): void {
  const scopeSleeve = targets.length === 1 ? targets[0] : undefined
  for (const entry of draft.accountBefore?.cash ?? []) {
    if (
      !scopeSleeve ||
      !entry.sleeve_id ||
      cashBelongsToSleeve(entry, scopeSleeve.sleeveId)
    ) {
      entry.currency = currency
    }
  }
  for (const sleeve of targets) {
    sleeve.state.globalConstraints.currencyConstraint =
      buildGlobalUniverseConstraint(currency)
  }
}

interface ProposalBuilderStore {
  // State
  state: ProposalBuilderState

  // The selected holdings-record key: a sleeve id, or the account id (meaning
  // the whole-account view). Sits at the store root (not inside `state`) so
  // poll-driven loadProposal reloads — which replace `state` wholesale —
  // can't clobber the user's selection.
  activeScope: SleeveID | AccountID

  // Actions
  reset: () => void
  setActiveScope: (scope: SleeveID | AccountID) => void
  loadProposal: (newState: ProposalBuilderState, configKeys?: string[]) => void
  resetDirtyCount: () => void
  markOptimizationRunning: (proposalId: string) => void
  createSleeve: (configKeys?: string[]) => SleeveID
  setProposalName: (proposalName: string) => void
  setSleeveName: (sleeveId: SleeveID, name: string) => void
  deleteSleeve: (sleeveId: SleeveID) => void
  setConstraints: (
    configKey: ConfigKey,
    field: Field,
    constraints: FieldConstraints,
    sleeveId?: SleeveID,
  ) => ProposalBuilderState
  setBatchConstraints: (
    updates: Array<{
      configKey: ConfigKey
      field: Field
      constraints: FieldConstraints
    }>,
    sleeveId?: SleeveID,
  ) => ProposalBuilderState
  setFilterListConstraints: (
    constraints: FilterListConstraints,
    sleeveId?: SleeveID,
  ) => ProposalBuilderState
  deleteFilterListConstraint: (
    configKey: ConfigKey,
    constraintType: FilterListConstraintType,
    constraintId: string,
    sleeveId?: SleeveID,
  ) => ProposalBuilderState
  deleteConstraint: (
    configKey: ConfigKey,
    field: Field,
    constraintId: string,
    sleeveId?: SleeveID,
  ) => ProposalBuilderState
  deleteConstraints: (
    deletes: Array<{
      configKey: ConfigKey
      field: Field
      constraintId: string
    }>,
  ) => ProposalBuilderState
  deleteOtherConstraint: (
    constraintId: string,
    sleeveId?: SleeveID,
  ) => ProposalBuilderState
  placeConstraint: (
    constraint: ModelConstraint,
    sleeveId?: SleeveID,
  ) => ProposalBuilderState
  deleteLadderPolicy: (sleeveId?: SleeveID) => ProposalBuilderState
  setLadderPolicy: (ladder: LadderPolicyConstraint) => void
  setAssignmentDate: (assignmentDate: string | undefined) => void
  blockIsin: (isin: string) => ProposalBuilderState
  blockIssuer: (issuer: string) => ProposalBuilderState
  setPriority: (
    configKey: ConfigKey,
    priority: Priority,
    sleeveId?: SleeveID,
  ) => void
  resetRiskFactor: (
    configKey: ConfigKey,
    riskFactor: RiskFactor,
    sleeveId?: SleeveID,
  ) => void
  setCashflowTargets: (targets: CashflowTarget[], sleeveId?: SleeveID) => void
  // `null` removes the band; ignored while the sleeve has no recurring target.
  setCashflowBand: (maxDeviationRatio: number | null) => void
  setCashflowBandPriority: (priority: Priority) => void
  setCashflowTargetPriority: (priority: Priority) => void
  setObjectiveStrategy: (strategy: ObjectiveStrategy | undefined) => void
  setCashBuffer: (cashBuffer: number) => void
  setDiversificationParameterConstraint: <
    K extends DiversificationParameterConstraintKey,
  >(
    key: K,
    value: DiversificationParameterConstraints[K],
    sleeveId?: SleeveID,
  ) => void
  setGlobalConstraints: (currency: Currency) => void
  setRiskGroup: (riskGroupId: string) => void
  setInvestorField: (
    field: InvestorInfoField,
    value: string | number | null,
  ) => ProposalBuilderState
  setInvestorTaxProfile: (
    taxProfile: TaxProfileValue | undefined,
  ) => ProposalBuilderState
  setSleeveCash: (sleeveId: string, cashAmount: number) => ProposalBuilderState
  setSleeveMaxSells: (
    sleeveId: string,
    maxSells: number | null,
  ) => ProposalBuilderState
  setSleeveMinimumOrderIncrement: (
    sleeveId: string,
    increment: number | null,
  ) => ProposalBuilderState
  setSleeveMinimumQuantity: (
    sleeveId: string,
    quantity: number | null,
  ) => ProposalBuilderState
  clearAllMaxSells: () => ProposalBuilderState
  setRebalancingEnabled: (enabled: boolean) => ProposalBuilderState
  setSleeveCompliance: (compliance: Record<SleeveID, SleeveCompliance>) => void
  setSleeveComplianceFromProposal: (proposal: DetailedProposal) => void
  setOrders: (orders: ProposalOrder[]) => void
  setSleeveStatisticsFromProposal: (proposal: DetailedProposal) => void
  setAccountAfterFromProposal: (proposal: DetailedProposal) => void
  setHoldPositionLock: (
    sleeveId: string,
    instrumentId: string,
    locked: boolean,
  ) => void
  toggleAllocationLock: (proposalAllocationId: string) => void
  toggleAllAllocationsLock: () => void
  updateAllocationQuantity: (
    proposalAllocationId: string,
    quantity: number,
  ) => void
  // Optimistically set a position's post-trade (after) quantity so the Proposed
  // column reflects an edit immediately; the server response reconciles it.
  setHoldingAfterQuantity: (
    sleeveId: string,
    instrumentId: string,
    quantity: number,
  ) => void
  updateOrderFields: (
    orderId: string,
    fields: {
      limit_price: number | null
      limit_ytm: number | null
      limit_ytw: number | null
      tax_effective_limit_ytw: number | null
      post_tax_limit_ytw: number | null
    },
  ) => void

  // Objective function actions
  setObjectiveTokens: (tokens: EquationToken[]) => void
}

// Stable empty array fallback so the holdings selectors return the same reference when the active sleeve has no entry.
const EMPTY_HOLDINGS: Holding[] = Object.freeze([] as Holding[]) as Holding[]

// Resolves the sleeve the active scope points at. Undefined in account view
// (scope = account id), which makes sleeve-scoped mutations no-op via their
// `if (!activeSleeve) return` guards.
export function findScopeSleeve(
  store: Pick<ProposalBuilderStore, 'state' | 'activeScope'>,
): Sleeve | undefined {
  return store.state.sleeves.find((s) => s.sleeveId === store.activeScope)
}

// Resolves an explicitly-addressed sleeve, falling back to the active scope.
// Explicit ids let account view (which has no scope sleeve) read and mutate a
// specific sleeve's rules.
export function resolveSleeve(
  store: Pick<ProposalBuilderStore, 'state' | 'activeScope'>,
  sleeveId?: SleeveID,
): Sleeve | undefined {
  if (!sleeveId) return findScopeSleeve(store)
  return store.state.sleeves.find((s) => s.sleeveId === sleeveId)
}

// Currency belongs to the sleeve. Account view has no scope sleeve, so it has a
// currency only while every sleeve agrees on one — never the first sleeve's,
// which would report one sleeve's currency over every other sleeve's numbers.
export function getScopedCurrency(
  store: Pick<ProposalBuilderStore, 'state' | 'activeScope'>,
): Currency | null {
  const scoped = getSleeveCurrency(findScopeSleeve(store))
  if (scoped) return scoped

  const currencies = store.state.sleeves.map((sleeve) =>
    getSleeveCurrency(sleeve),
  )
  const [first] = currencies
  if (!first) return null
  return currencies.every((currency) => currency === first) ? first : null
}

// Currency to format money in. Where there is none to read — a proposal that
// hasn't hydrated, or an account view whose sleeves disagree — no sleeve's
// currency is the right answer, so formatting falls back to the app default.
export function getFormattingCurrency(
  store: Pick<ProposalBuilderStore, 'state' | 'activeScope'>,
): string {
  return getScopedCurrency(store) ?? DEFAULT_INVESTOR_INFO.currency
}

const useProposalBuilderStoreBase = create<ProposalBuilderStore>()(
  devtools(
    immer((set, get) => ({
      // Initial state
      state: createInitialState(),
      activeScope: DEFAULT_SLEEVE_ID,

      reset: () => {
        set((draft) => {
          draft.state = createInitialState()
          draft.activeScope = DEFAULT_SLEEVE_ID
        })
      },

      setActiveScope: (scope) => {
        const { state } = get()
        const isAccountScope = Boolean(scope) && scope === state.activeAccount
        const isSleeveScope = state.sleeves.some((s) => s.sleeveId === scope)
        if (!isAccountScope && !isSleeveScope) {
          globalLogger.warn('Unknown scope', { scope })
          return
        }
        set((draft) => {
          draft.activeScope = scope
        })
      },

      // Seeding is idempotent and deliberately does not bump dirtyCount, so it
      // never triggers auto-save.
      loadProposal: (newState: ProposalBuilderState, configKeys = []) => {
        set((draft) => {
          draft.state = newState
          draft.state.flags.dirtyCount = 0
          for (const sleeve of draft.state.sleeves) {
            for (const configKey of configKeys) {
              getOrCreateRiskFactor(sleeve, configKey as ConfigKey)
            }
          }
          // Self-heal the scope: a proposal switch (or a reload that dropped
          // a sleeve) can leave it dangling; snap to the first sleeve.
          const scopeIsValid =
            draft.activeScope === draft.state.activeAccount ||
            draft.state.sleeves.some((s) => s.sleeveId === draft.activeScope)
          if (!scopeIsValid) {
            draft.activeScope =
              draft.state.sleeves[0]?.sleeveId ?? DEFAULT_SLEEVE_ID
          }
        })
      },
      resetDirtyCount: () => {
        set((draft) => {
          draft.state.flags.dirtyCount = 0
        })
      },
      markOptimizationRunning: (proposalId) => {
        if (get().state.proposalId !== proposalId) return
        set((draft) => {
          draft.state.proposalStatus = ProposalStatus.running
        })
      },
      // Seed like loadProposal; keys come from the caller because the store
      // can't read the React config hook.
      createSleeve: (configKeys = []) => {
        // IMPORTANT: Prefix sleeve IDs with 'slv_' to ensure consistency and to prevent the backend from re-ordering sleeves.
        const newSleeveId = `slv_${generateId()}`
        set((draft) => {
          const sleeveNumber = draft.state.sleeves.length + 1
          const sleeve = createSleeve(
            newSleeveId,
            `Sleeve ${sleeveNumber}`,
            getScopedCurrency(draft) ?? undefined,
          )
          for (const configKey of configKeys) {
            getOrCreateRiskFactor(sleeve, configKey as ConfigKey)
          }
          draft.state.sleeves.push(sleeve)
          draft.state.flags.dirtyCount += 1
          // Creating a sleeve focuses it, which exits the account view.
          draft.activeScope = newSleeveId
        })
        return newSleeveId
      },

      deleteSleeve: (sleeveId) => {
        set((draft) => {
          if (draft.state.sleeves.length <= 1) return

          // Drop the deleted sleeve's cash lot.
          if (draft.state.accountBefore?.cash) {
            draft.state.accountBefore.cash =
              draft.state.accountBefore.cash.filter(
                (entry) => !cashBelongsToSleeve(entry, sleeveId),
              )
          }

          if (draft.activeScope === sleeveId) {
            const remaining = draft.state.sleeves.find(
              (s) => s.sleeveId !== sleeveId,
            )
            if (remaining) draft.activeScope = remaining.sleeveId
          }

          draft.state.sleeves = draft.state.sleeves.filter(
            (s) => s.sleeveId !== sleeveId,
          )

          delete draft.state.sleeveCompliance[sleeveId]
          delete draft.state.sleeveStatistics[sleeveId]

          for (const order of draft.state.orders) {
            order.allocations = order.allocations.filter(
              (a) => a.sleeve_id !== sleeveId,
            )
          }
          draft.state.orders = draft.state.orders.filter(
            (o) => o.allocations.length > 0,
          )

          draft.state.flags.dirtyCount += 1
        })
      },

      setProposalName: (proposalName: string) => {
        set((draft) => {
          draft.state.proposalName = proposalName
          draft.state.flags.dirtyCount += 1
        })
      },

      setSleeveName: (sleeveId: SleeveID, name: string) => {
        set((draft) => {
          const sleeve = draft.state.sleeves.find(
            (s) => s.sleeveId === sleeveId,
          )
          if (!sleeve) return
          sleeve.name = name
          draft.state.flags.dirtyCount += 1
        })
      },

      // Set constraints - returns updated state synchronously
      setConstraints: (configKey, field, constraints, sleeveId) => {
        set((draft) => {
          const sleeve = resolveSleeve(draft, sleeveId)
          if (!sleeve) return

          // Skip FilterLists - use setFilterListConstraints instead
          if (configKey === ConfigKey.FilterLists) return

          const riskFactor = getOrCreateRiskFactor(sleeve, configKey)

          // Create field if it doesn't exist
          let fieldConfig = riskFactor.fields[field]
          if (!fieldConfig) {
            fieldConfig = createEmptyFieldConstraints()
            riskFactor.fields[field] = fieldConfig
          }

          fieldConfig.universeConstraint = constraints.universeConstraint
          fieldConfig.diversificationConstraint =
            constraints.diversificationConstraint
          fieldConfig.piecewiseDiversificationConstraint =
            constraints.piecewiseDiversificationConstraint
          fieldConfig.averageValueConstraint =
            constraints.averageValueConstraint
          fieldConfig.allocationConstraints = constraints.allocationConstraints
          draft.state.flags.dirtyCount += 1
        })

        // Return fresh state from store
        return get().state
      },

      // Set batch constraints - applies multiple constraint updates in a single state update
      setBatchConstraints: (updates, sleeveId) => {
        set((draft) => {
          const sleeve = resolveSleeve(draft, sleeveId)
          if (!sleeve) return

          // Apply all updates in a single transaction
          updates.forEach(({ configKey, field, constraints }) => {
            // Skip FilterLists - use setFilterListConstraints instead
            if (configKey === ConfigKey.FilterLists) return

            const riskFactor = getOrCreateRiskFactor(sleeve, configKey)

            // Create field if it doesn't exist
            let fieldConfig = riskFactor.fields[field]
            if (!fieldConfig) {
              fieldConfig = createEmptyFieldConstraints()
              riskFactor.fields[field] = fieldConfig
            }

            fieldConfig.universeConstraint = constraints.universeConstraint
            fieldConfig.diversificationConstraint =
              constraints.diversificationConstraint
            fieldConfig.piecewiseDiversificationConstraint =
              constraints.piecewiseDiversificationConstraint
            fieldConfig.averageValueConstraint =
              constraints.averageValueConstraint
            fieldConfig.allocationConstraints =
              constraints.allocationConstraints
          })
          draft.state.flags.dirtyCount += 1
        })

        // Return fresh state from store
        return get().state
      },

      // Set filter list constraints - specialized for FilterLists risk factor
      setFilterListConstraints: (constraints, sleeveId) => {
        set((draft) => {
          const sleeve = resolveSleeve(draft, sleeveId)
          if (!sleeve) return

          const riskFactor = getOrCreateRiskFactor(
            sleeve,
            ConfigKey.FilterLists,
          )

          riskFactor.filterListConstraints = constraints
          draft.state.flags.dirtyCount += 1
        })

        return get().state
      },

      // Delete filter list constraint
      deleteFilterListConstraint: (
        _configKey,
        _constraintType,
        constraintId,
        sleeveId,
      ) => {
        set((draft) => {
          const sleeve = resolveSleeve(draft, sleeveId)
          if (!sleeve) return

          removeConstraintFromTree(sleeve.state, constraintId)
          draft.state.flags.dirtyCount += 1
        })

        return get().state
      },

      // Delete constraint
      deleteConstraint: (_configKey, _field, constraintId, sleeveId) => {
        set((draft) => {
          const sleeve = resolveSleeve(draft, sleeveId)
          if (!sleeve) return

          removeConstraintFromTree(sleeve.state, constraintId)
          draft.state.flags.dirtyCount += 1
        })

        // Return fresh state from store
        return get().state
      },

      // Batch delete constraints
      deleteConstraints: (deletes) => {
        set((draft) => {
          const activeSleeve = findScopeSleeve(draft)
          if (!activeSleeve) return

          for (const { constraintId } of deletes) {
            removeConstraintFromTree(activeSleeve.state, constraintId)
          }
          draft.state.flags.dirtyCount += 1
        })

        return get().state
      },

      // Delete a constraint from a sleeve's otherConstraints
      deleteOtherConstraint: (constraintId, sleeveId) => {
        set((draft) => {
          const sleeve = resolveSleeve(draft, sleeveId)
          if (!sleeve) return

          sleeve.state.otherConstraints = sleeve.state.otherConstraints.filter(
            (c) => c.id !== constraintId,
          )
          draft.state.flags.dirtyCount += 1
        })

        return get().state
      },

      // Places a newly created custom rule (re-placing first when it carries
      // an id): routed through the same placement ingestion uses, so a rule
      // lands in the same slot on create as it would on reload.
      placeConstraint: (constraint, sleeveId) => {
        set((draft) => {
          const sleeve = resolveSleeve(draft, sleeveId)
          if (!sleeve) return

          if (constraint.id) {
            removeConstraintFromTree(sleeve.state, constraint.id)
            removeDiversificationParameterConstraintById(
              sleeve.state.diversificationParameterConstraints,
              constraint.id,
            )
          }

          // A currency-only rule is the sleeve's currency in rule form: write it
          // through the currency path (which also syncs the sleeve's cash lot)
          // rather than placing it as an uneditable "other rule".
          const strayCurrency = readCurrencyConstraint(constraint)
          if (strayCurrency) {
            writeSleeveCurrency(draft.state, strayCurrency, [sleeve])
            draft.state.flags.dirtyCount += 1
            return
          }

          const placed = { ...constraint, id: constraint.id ?? generateId() }
          // Place by field mapping regardless of scope, matching ingestion, so a
          // rule lands in the same slot on create as on reload. Slot-shaped
          // diversification constraints home into their empty parameter slot
          // (surfacing in both the sidepane and the rules view); everything
          // else lands in the risk-factor tree. The serializer
          // resolves the scope (coerce to the factor default when absent, keep
          // the rule's own otherwise); a single scoped field-mappable rule homes
          // into its factor rather than becoming an "other constraint".
          if (
            !placeDiversificationParameterConstraint(
              sleeve.state.diversificationParameterConstraints,
              placed,
            )
          ) {
            placeConstraintInTree({
              target: sleeve.state,
              constraint: placed,
            })
          }
          draft.state.flags.dirtyCount += 1
        })

        return get().state
      },

      // Delete a sleeve's ladder policy
      deleteLadderPolicy: (sleeveId) => {
        set((draft) => {
          const sleeve = resolveSleeve(draft, sleeveId)
          if (!sleeve) return

          sleeve.state.ladderPolicy = undefined
          draft.state.flags.dirtyCount += 1
        })

        return get().state
      },

      // Block an ISIN by adding/extending a universe exclusion in otherConstraints.
      // Assumes a flat filter shape: { [Field.Isin]: { not_in: [...] } }
      blockIsin: (isin) => {
        set((draft) => {
          const activeSleeve = findScopeSleeve(draft)
          if (!activeSleeve) return

          const existingIdx = activeSleeve.state.otherConstraints.findIndex(
            (c) => {
              if (c.type !== UniverseConstraintType.universe) return false
              const filter = (c as UniverseConstraint).filter as FilterCondition
              return filter?.[Field.Isin] != null
            },
          )

          if (existingIdx >= 0) {
            const uc = activeSleeve.state.otherConstraints[
              existingIdx
            ] as UniverseConstraint
            const filter = uc.filter as FilterCondition
            const existingNotIn =
              (filter?.[Field.Isin]?.[Operator.NotIn] as string[]) ?? []
            if (existingNotIn.includes(isin)) return
            uc.filter = {
              [Field.Isin]: {
                [Operator.NotIn]: [...existingNotIn, isin],
              },
            }
          } else {
            activeSleeve.state.otherConstraints.push({
              id: generateId(),
              type: UniverseConstraintType.universe,
              filter: {
                [Field.Isin]: { [Operator.NotIn]: [isin] },
              },
            })
          }
          draft.state.flags.dirtyCount += 1
        })

        return get().state
      },

      // Block an issuer by extending the Issuer risk factor's NotIn universe constraint.
      // Assumes a flat filter shape: { [Field.Issuer]: { not_in: [...] } }
      blockIssuer: (issuer) => {
        set((draft) => {
          const activeSleeve = findScopeSleeve(draft)
          if (!activeSleeve) return

          const riskFactor = getOrCreateRiskFactor(
            activeSleeve,
            ConfigKey.Issuer,
          )

          let fieldConfig = riskFactor.fields[Field.Issuer]
          if (!fieldConfig) {
            fieldConfig = createEmptyFieldConstraints()
            riskFactor.fields[Field.Issuer] = fieldConfig
          }

          const filter = fieldConfig.universeConstraint?.filter as
            | FilterCondition
            | undefined
          const existingNotIn =
            (filter?.[Field.Issuer]?.[Operator.NotIn] as string[]) ?? []

          if (existingNotIn.includes(issuer)) return

          fieldConfig.universeConstraint = {
            id: fieldConfig.universeConstraint?.id ?? generateId(),
            type: UniverseConstraintType.universe,
            filter: {
              [Field.Issuer]: {
                [Operator.NotIn]: [...existingNotIn, issuer],
              },
            },
            constraint_priority:
              fieldConfig.universeConstraint?.constraint_priority,
          }
          draft.state.flags.dirtyCount += 1
        })

        return get().state
      },

      // Set priority
      setPriority: (configKey, priority, sleeveId) => {
        set((draft) => {
          const sleeve = resolveSleeve(draft, sleeveId)
          if (!sleeve) return

          const riskFactor = getOrCreateRiskFactor(sleeve, configKey)

          riskFactor.priorityLevel = priority
          // Per-constraint priority is authoritative on the wire, so a
          // factor-level edit writes through to every constraint it holds.
          eachConstraintInFactor(riskFactor, (constraint) => {
            constraint.constraint_priority = priority
          })
          draft.state.flags.dirtyCount += 1
        })
      },

      // Reset risk factor to a previous state
      resetRiskFactor: (configKey, riskFactor, sleeveId) => {
        set((draft) => {
          const sleeve = resolveSleeve(draft, sleeveId)
          if (!sleeve) return

          sleeve.state.riskFactors[configKey] = riskFactor
          draft.state.flags.dirtyCount += 1
        })
      },

      // Set cashflow targets. The band only levels recurring targets, so it
      // goes with the last one.
      setCashflowTargets: (targets, sleeveId) => {
        set((draft) => {
          const sleeve = resolveSleeve(draft, sleeveId)
          if (!sleeve) return
          sleeve.state.cashflowTargets = targets
          if (!hasRecurringCashflowTarget(targets)) {
            sleeve.state.otherConstraints = applyCashflowBand(
              sleeve.state.otherConstraints,
              null,
            )
          }
          draft.state.flags.dirtyCount += 1
        })
      },

      setCashflowBand: (maxDeviationRatio) => {
        set((draft) => {
          const sleeve = findScopeSleeve(draft)
          if (!sleeve) return
          const ratio = hasRecurringCashflowTarget(sleeve.state.cashflowTargets)
            ? maxDeviationRatio
            : null
          const current = readCashflowBand(sleeve.state.otherConstraints)
          if ((current?.max_deviation_ratio ?? null) === ratio) return
          sleeve.state.otherConstraints = applyCashflowBand(
            sleeve.state.otherConstraints,
            ratio,
          )
          draft.state.flags.dirtyCount += 1
        })
      },

      setCashflowBandPriority: (priority) => {
        set((draft) => {
          const sleeve = findScopeSleeve(draft)
          if (!sleeve) return
          const current = readCashflowBand(sleeve.state.otherConstraints)
          if (!current) return
          const stored =
            parsePriority(current.constraint_priority) ?? Priority.Low
          if (stored === priority) return
          sleeve.state.otherConstraints = applyCashflowBandPriority(
            sleeve.state.otherConstraints,
            priority,
          )
          draft.state.flags.dirtyCount += 1
        })
      },

      setCashflowTargetPriority: (priority) => {
        set((draft) => {
          const sleeve = findScopeSleeve(draft)
          if (!sleeve) return
          if (sleeve.state.cashflowTargetPriority === priority) return
          sleeve.state.cashflowTargetPriority = priority
          draft.state.flags.dirtyCount += 1
        })
      },

      setObjectiveStrategy: (strategy) => {
        set((draft) => {
          const activeSleeve = findScopeSleeve(draft)
          if (!activeSleeve) return
          activeSleeve.state.objectiveStrategy = strategy
          draft.state.flags.dirtyCount += 1
        })
      },

      setLadderPolicy: (ladder) => {
        set((draft) => {
          const activeSleeve = findScopeSleeve(draft)
          if (!activeSleeve) return
          const existing = activeSleeve.state.ladderPolicy
          activeSleeve.state.ladderPolicy = {
            ...ladder,
            // Preserve the existing constraint id on edit so the round-trip
            // through ingestion/emission stays stable (constraint-id-preservation).
            id: existing?.id ?? generateId(),
          }
          draft.state.flags.dirtyCount += 1
        })
      },

      // Set the active sleeve's ladder assignment date (the top-level reference
      // date emitted as model_assignment.assignment_date). undefined = use today.
      setAssignmentDate: (assignmentDate) => {
        set((draft) => {
          const activeSleeve = findScopeSleeve(draft)
          if (!activeSleeve) return
          activeSleeve.state.assignmentDate = assignmentDate
          draft.state.flags.dirtyCount += 1
        })
      },

      // Set the active sleeve's cash buffer (fraction in [0, 1]).
      setCashBuffer: (cashBuffer) => {
        set((draft) => {
          const activeSleeve = findScopeSleeve(draft)
          if (!activeSleeve) return
          activeSleeve.state.cashBuffer = cashBuffer
          draft.state.flags.dirtyCount += 1
        })
      },

      setDiversificationParameterConstraint: (key, value, sleeveId) => {
        set((draft) => {
          const sleeve = resolveSleeve(draft, sleeveId)
          if (!sleeve) return
          sleeve.state.diversificationParameterConstraints[key] = value
          draft.state.flags.dirtyCount += 1
        })
      },

      setGlobalConstraints: (currency) => {
        set((draft) => {
          for (const sleeve of draft.state.sleeves) {
            sleeve.state.globalConstraints =
              createDefaultGlobalConstraints(currency)
          }
          draft.state.flags.dirtyCount += 1
        })
      },

      setRiskGroup: (riskGroupId) => {
        const { accountBefore } = get().state
        if (!accountBefore) {
          return
        }
        set((draft) => {
          if (!draft.state.accountBefore) {
            return
          }
          draft.state.accountBefore.moment_trading_group_id = riskGroupId
          draft.state.flags.dirtyCount += 1
        })
      },

      // Set investor field (updates a single field in accountBefore.investor)
      setInvestorField: (field, value) => {
        set((draft) => {
          if (!draft.state.accountBefore?.investor) {
            return
          }

          switch (field) {
            case InvestorInfoField.InvestorName:
              draft.state.accountBefore.investor.name = value as string
              break
            case InvestorInfoField.StateOfResidence:
              draft.state.accountBefore.investor.tax_state = value as string
              break
            case InvestorInfoField.Currency: {
              const currency = value as Currency
              const scopeSleeve = findScopeSleeve(draft)
              if (
                !draft.state.accountBefore.cash ||
                draft.state.accountBefore.cash.length === 0
              ) {
                const cashObj = createInitialCashObject()
                cashObj.settled_open_quantity =
                  DEFAULT_INVESTOR_INFO.total_investment
                if (scopeSleeve) cashObj.sleeve_id = scopeSleeve.sleeveId
                draft.state.accountBefore.cash = [cashObj]
              }
              writeSleeveCurrency(
                draft.state,
                currency,
                scopeSleeve ? [scopeSleeve] : draft.state.sleeves,
              )
              break
            }
            case InvestorInfoField.FederalIncomeTax:
              draft.state.accountBefore.investor.marginal_ordinary_taxes.federal_tax_rate =
                value as number
              draft.state.accountBefore.investor.marginal_long_term_taxes.federal_tax_rate =
                value as number
              break
            case InvestorInfoField.StateIncomeTax:
              draft.state.accountBefore.investor.marginal_ordinary_taxes.state_tax_rate =
                value as number
              draft.state.accountBefore.investor.marginal_long_term_taxes.state_tax_rate =
                value as number
              break
            case InvestorInfoField.TotalInvestment:
              if (
                !draft.state.accountBefore.cash ||
                draft.state.accountBefore.cash.length === 0
              ) {
                draft.state.accountBefore.cash = [createInitialCashObject()]
              }
              // Distribute the new total across all cash entries
              // proportional to each entry's current share. For the legacy
              // single-entry shape this writes the full amount to the lone
              // entry; for the DoD multi-entry shape it keeps the per-sleeve
              // breakdown in sync.
              draft.state.accountBefore.cash = distributeCashProportionally(
                draft.state.accountBefore.cash,
                value as number,
              )
              break
            case InvestorInfoField.MinimumOrderIncrement:
              // Handled per-sleeve in the sleeve section; no account-level write.
              break
            case InvestorInfoField.MinimumQuantity:
              // Handled per-sleeve in the sleeve section; no account-level write.
              break
            case InvestorInfoField.CustomIncomeTax:
              draft.state.accountBefore.investor.custom_income_tax =
                value as number
              break
            case InvestorInfoField.CustomCapitalGainsTax:
              draft.state.accountBefore.investor.custom_capital_gains_tax =
                value as number
              break
            // Note: CountryOfResidence are not stored
            // in the API Investor type, so we skip them
            default:
              break
          }
          draft.state.flags.dirtyCount += 1
        })

        // Return fresh state from store
        return get().state
      },

      // None persists as non_taxable; don't coerce it to us_taxable.
      setInvestorTaxProfile: (taxProfile) => {
        set((draft) => {
          if (!draft.state.accountBefore?.investor) return
          draft.state.accountBefore.investor.tax_profile = taxProfile
          draft.state.flags.dirtyCount += 1
        })
        return get().state
      },

      // Sleeve cash is the source of truth — the allocation input writes it
      // directly, not target allocation (PORT-2902).
      setSleeveCash: (sleeveId, cashAmount) => {
        set((draft) => {
          const sleeve = draft.state.sleeves.find(
            (s) => s.sleeveId === sleeveId,
          )
          if (!sleeve) {
            globalLogger.warn('Sleeve not found', { sleeveId })
            return
          }
          if (!draft.state.accountBefore) return

          const cash = draft.state.accountBefore.cash ?? []
          const entry = cash.find((c) => cashBelongsToSleeve(c, sleeveId))
          if (entry) {
            entry.settled_open_quantity = cashAmount
          } else {
            cash.push({
              ...createInitialCashObject(),
              currency:
                getSleeveCurrency(sleeve) ?? DEFAULT_INVESTOR_INFO.currency,
              settled_open_quantity: cashAmount,
              sleeve_id: sleeveId,
            })
          }
          draft.state.accountBefore.cash = cash
          draft.state.flags.dirtyCount += 1
        })

        return get().state
      },

      setSleeveMaxSells: (sleeveId, maxSells) => {
        set((draft) => {
          const sleeve = draft.state.sleeves.find(
            (s) => s.sleeveId === sleeveId,
          )
          if (!sleeve) {
            globalLogger.warn('Sleeve not found', { sleeveId })
            return
          }

          sleeve.state.otherConstraints = applyMaxSells(
            sleeve.state.otherConstraints,
            maxSells,
          )
          draft.state.flags.dirtyCount += 1
        })

        return get().state
      },

      setSleeveMinimumOrderIncrement: (sleeveId, increment) => {
        set((draft) => {
          const sleeve = draft.state.sleeves.find(
            (s) => s.sleeveId === sleeveId,
          )
          if (!sleeve) {
            globalLogger.warn('Sleeve not found', { sleeveId })
            return
          }
          sleeve.state.minimum_order_increment = increment
          draft.state.flags.dirtyCount += 1
        })
        return get().state
      },

      setSleeveMinimumQuantity: (sleeveId, quantity) => {
        set((draft) => {
          const sleeve = draft.state.sleeves.find(
            (s) => s.sleeveId === sleeveId,
          )
          if (!sleeve) {
            globalLogger.warn('Sleeve not found', { sleeveId })
            return
          }
          sleeve.state.otherConstraints = applyMinimumQuantity(
            sleeve.state.otherConstraints,
            quantity ?? 0,
            generateId,
          )
          draft.state.flags.dirtyCount += 1
        })
        return get().state
      },

      clearAllMaxSells: () => {
        set((draft) => {
          let removedAny = false
          for (const sleeve of draft.state.sleeves) {
            if (readMaxSells(sleeve.state.otherConstraints) === null) continue
            sleeve.state.otherConstraints = applyMaxSells(
              sleeve.state.otherConstraints,
              null,
            )
            removedAny = true
          }
          if (removedAny) {
            draft.state.flags.dirtyCount += 1
          }
        })

        return get().state
      },

      setRebalancingEnabled: (enabled) => {
        const strategy = enabled
          ? RebalanceOptions.Enabled
          : RebalanceOptions.Never
        set((draft) => {
          let changedAny = false
          for (const sleeve of draft.state.sleeves) {
            if (sleeve.state.rebalanceStrategy === strategy) continue
            sleeve.state.rebalanceStrategy = strategy
            changedAny = true
          }
          if (changedAny) {
            draft.state.flags.dirtyCount += 1
          }
        })

        return get().state
      },

      // Set sleeve compliance (server-computed, doesn't trigger auto-save)
      setSleeveCompliance: (compliance) => {
        set((draft) => {
          draft.state.sleeveCompliance = compliance
        })
      },

      setSleeveComplianceFromProposal: (proposal) => {
        set((draft) => {
          const next = extractSleeveCompliance(proposal)
          for (const [sleeveId, compliance] of Object.entries(next)) {
            draft.state.sleeveCompliance[sleeveId] = compliance
          }
        })
      },

      // Replace orders array with fresh data from API response
      setOrders: (orders) => {
        set((draft) => {
          draft.state.orders = orders
        })
      },

      // Replace-by-key only — sleeves absent from the response are left untouched to avoid clearing unrelated sleeves.
      setSleeveStatisticsFromProposal: (proposal) => {
        set((draft) => {
          const next = extractSleeveStatistics(proposal)
          for (const [sleeveId, stats] of Object.entries(next)) {
            draft.state.sleeveStatistics[sleeveId] = stats
          }
          // Same guard as above: a response without accounts leaves the account stats untouched.
          if (Object.keys(proposal.accounts).length > 0) {
            draft.state.accountStatistics = extractAccountStatistics(proposal)
          }
        })
      },

      // Refresh accountAfter + holdingsAfter cache from a BE proposal response so risk-factor consumers see fresh ISINs without polling lag.
      setAccountAfterFromProposal: (proposal) => {
        set((draft) => {
          // The applied response IS the server's latest version; stamping it
          // keeps the status poll's version comparison quiet so an edit
          // doesn't trigger a redundant full-proposal refetch.
          draft.state.version = proposal.version ?? draft.state.version
          // A save can also transition status server-side (e.g. expired ->
          // in_review); without stamping it the poll sees a permanent
          // status mismatch and refetches on every tick.
          draft.state.proposalStatus =
            proposal.proposal_status ?? draft.state.proposalStatus
          const accountId = draft.state.activeAccount
          const account = proposal.accounts?.[accountId]
          if (!account) return
          draft.state.accountAfter = account.after ?? null
          const { holdingsAfter, holdingsHeld } = computeInitialHoldings(
            proposal,
            accountId,
          )
          draft.state.holdingsAfter = holdingsAfter
          draft.state.holdingsHeld = holdingsHeld
          draft.state.proposalActions = proposal.proposal_actions ?? []
        })
      },

      // Targeted optimistic write of a single after-holding's quantity (no-op if not yet cached).
      // Caution: don't adopt this pattern widely — it exists only to prevent client-side flicker.
      setHoldingAfterQuantity: (sleeveId, instrumentId, quantity) => {
        set((draft) => {
          const holding = draft.state.holdingsAfter[sleeveId]?.find(
            (h) => h.instrument_id === instrumentId,
          )
          if (!holding) return
          holding.quantity = quantity
        })
      },

      // Optimistic mirror of ApplyProposalAction's position-level lock/clear
      // replace semantics; the next poll reload reconciles with the server.
      setHoldPositionLock: (sleeveId, instrumentId, locked) => {
        set((draft) => {
          const rest = draft.state.proposalActions.filter(
            (a) =>
              !(
                a.action === 'lock' &&
                a.sleeve_id === sleeveId &&
                a.instrument_id === instrumentId &&
                !a.moment_lot_id
              ),
          )
          if (locked) {
            rest.push({
              action: 'lock',
              sleeve_id: sleeveId,
              instrument_id: instrumentId,
            })
          }
          draft.state.proposalActions = rest
        })
      },

      // Toggle lock on a specific allocation
      toggleAllocationLock: (proposalAllocationId) => {
        set((draft) => {
          const result = findAllocationInOrders(
            draft.state.orders,
            proposalAllocationId,
          )
          if (result) {
            result.allocation.lock = !result.allocation.lock
            draft.state.flags.dirtyCount += 1
          }
        })
      },

      // Toggle lock on all allocations in the active sleeve
      toggleAllAllocationsLock: () => {
        set((draft) => {
          const { activeAccount } = draft.state
          const activeSleeve = findScopeSleeve(draft)
          if (!activeSleeve) return
          let hasMatch = false
          let hasUnlocked = false
          for (const order of draft.state.orders) {
            for (const alloc of order.allocations) {
              if (
                alloc.moment_account_id === activeAccount &&
                alloc.sleeve_id === activeSleeve.sleeveId
              ) {
                hasMatch = true
                if (!alloc.lock) hasUnlocked = true
              }
            }
          }
          if (!hasMatch) return

          const newLockState = hasUnlocked
          for (const order of draft.state.orders) {
            for (const alloc of order.allocations) {
              if (
                alloc.moment_account_id === activeAccount &&
                alloc.sleeve_id === activeSleeve.sleeveId
              ) {
                alloc.lock = newLockState
              }
            }
          }
          draft.state.flags.dirtyCount += 1
        })
      },

      // Sync allocation quantity locally after the server PATCH/DELETE has already succeeded.
      // Does NOT increment dirtyCount — the server is already up-to-date, so no auto-save needed.
      updateAllocationQuantity: (proposalAllocationId, quantity) => {
        set((draft) => {
          const result = findAllocationInOrders(
            draft.state.orders,
            proposalAllocationId,
          )
          if (!result) return

          if (quantity === 0) {
            // Remove the allocation from the order
            const allocIndex = result.order.allocations.findIndex(
              (a) => a.proposal_allocation_id === proposalAllocationId,
            )
            if (allocIndex !== -1) {
              result.order.allocations.splice(allocIndex, 1)
            }
            // Recalculate order total quantity
            result.order.quantity = result.order.allocations.reduce(
              (sum, a) => sum + a.quantity,
              0,
            )
            // Remove the order entirely if no allocations remain
            if (result.order.allocations.length === 0) {
              const orderIndex = draft.state.orders.findIndex(
                (o) => o.moment_order_id === result.order.moment_order_id,
              )
              if (orderIndex !== -1) {
                draft.state.orders.splice(orderIndex, 1)
              }
            }
          } else {
            result.allocation.quantity = quantity
            result.allocation.lock = true
            // market_value/net_money are server-authoritative — synced via
            // setOrders(result.orders) from the mutation's DetailedProposal
            // response, never recomputed client-side.
            result.order.quantity = result.order.allocations.reduce(
              (sum, a) => sum + a.quantity,
              0,
            )
          }
        })
      },

      // Sync server-confirmed order fields (e.g. paired limit price/YTW after conversion)
      updateOrderFields: (orderId, fields) => {
        set((draft) => {
          const order = draft.state.orders.find(
            (o) => o.moment_order_id === orderId,
          )
          if (!order) return
          order.limit_price = fields.limit_price ?? order.limit_price
          order.limit_ytm = fields.limit_ytm ?? order.limit_ytm
          order.limit_ytw = fields.limit_ytw ?? order.limit_ytw
          order.tax_effective_limit_ytw =
            fields.tax_effective_limit_ytw ?? order.tax_effective_limit_ytw
          order.post_tax_limit_ytw =
            fields.post_tax_limit_ytw ?? order.post_tax_limit_ytw
          // Allocation market_value/net_money are server-authoritative —
          // synced via setOrders(result.orders) from the mutation's
          // DetailedProposal response, never recomputed client-side.
        })
      },

      // Objective function actions
      setObjectiveTokens: (tokens) => {
        set((draft) => {
          const activeSleeve = findScopeSleeve(draft)
          if (!activeSleeve) return
          activeSleeve.state.objectiveTokens = tokens
          draft.state.flags.dirtyCount += 1
        })
      },
    })),
    { name: 'proposal-builder-store' },
  ),
)

// Primitives
function isPosition(holding: Holding): boolean {
  return !isCashInstrumentId(holding.instrument_id)
}

// The holdings records carry the account-wide aggregation under the
// account-id key alongside the sleeve entries, so the active scope maps
// directly to a record key.
function getHoldingsBefore(store: ProposalBuilderStore): Holding[] {
  return store.state.holdingsBefore[store.activeScope] ?? EMPTY_HOLDINGS
}

function getHoldingsAfter(store: ProposalBuilderStore): Holding[] {
  return store.state.holdingsAfter[store.activeScope] ?? EMPTY_HOLDINGS
}

// Sleeve-money helpers resolve via the scope sleeve; in account view they
// return empty/0 (except cash, which sums account-wide).
function getSleeveAllocations(store: ProposalBuilderStore): Allocation[] {
  const activeSleeve = findScopeSleeve(store)
  if (!activeSleeve) return []
  return store.state.orders
    .flatMap((order) => order.allocations)
    .filter(
      (a) =>
        a.moment_account_id === store.state.activeAccount &&
        a.sleeve_id === activeSleeve.sleeveId,
    )
}

function getSleeveMarketValue(store: ProposalBuilderStore): number {
  return getSleeveAllocations(store).reduce(
    (sum, a) => sum + allocationNetMoney(a),
    0,
  )
}

function getScopedCash(store: ProposalBuilderStore): number {
  // Post-trade residual lives on accountAfter; accountBefore holds the editable
  // budget. Fall back to before until after is populated (PORT-2902).
  const cash = store.state.accountAfter?.cash?.length
    ? store.state.accountAfter.cash
    : store.state.accountBefore?.cash
  if (store.activeScope === store.state.activeAccount) {
    // Naive sum across every sleeve's entries — multi-currency mixing is out
    // of scope (PORT-3460).
    return calculateTotalAccountCash(cash)
  }
  const activeSleeve = findScopeSleeve(store)
  if (!activeSleeve) return 0
  return calculateSleeveCash(cash, activeSleeve.sleeveId)
}

// Starting (budget) cash from accountBefore, ignoring the post-trade residual.
export function getScopedStartingCash(store: ProposalBuilderStore): number {
  const cash = store.state.accountBefore?.cash
  if (store.activeScope === store.state.activeAccount) {
    // Naive sum across every sleeve's entries — multi-currency mixing is out
    // of scope (PORT-3460).
    return calculateTotalAccountCash(cash)
  }
  const activeSleeve = findScopeSleeve(store)
  if (!activeSleeve) return 0
  return calculateSleeveCash(cash, activeSleeve.sleeveId)
}

// Sell proceeds only — buys intentionally don't reduce the total (PORT-3184).
// Uses allocation market values (optimistically recalculated client-side on
// edits) so the total tracks order changes without waiting for the BE's
// accountAfter refresh.
function getSellOrdersProceeds(store: ProposalBuilderStore): number {
  return store.state.orders.reduce((sum, order) => {
    if (order.side !== OrderSide.Sell) return sum
    const orderMarketValue = order.allocations.reduce(
      (total, allocation) => total + allocationNetMoney(allocation),
      0,
    )
    return sum + orderMarketValue
  }, 0)
}

// Returns orders scoped to the active scope, with each order's allocations
// filtered to that scope. Orders with no matching allocations are excluded.
function getScopedOrdersAndAllocations(
  store: ProposalBuilderStore,
): ProposalOrder[] {
  const { orders, activeAccount } = store.state
  if (!orders.length || !activeAccount) return []
  // Single-account assumption: every order belongs to the active account,
  // so account view needs no allocation filtering.
  if (store.activeScope === activeAccount) return orders
  return orders
    .map((order) => ({
      ...order,
      allocations: order.allocations.filter(
        (alloc) =>
          alloc.moment_account_id === activeAccount &&
          alloc.sleeve_id === store.activeScope,
      ),
    }))
    .filter((order) => order.allocations.length > 0)
}

// Derived selectors
export const useProposalBuilderStoreV2 = createSelectors(
  useProposalBuilderStoreBase,
  {
    activeView: (state): ProposalBuilderView =>
      state.activeScope === state.state.activeAccount ? 'account' : 'sleeve',
    // The scope sleeve — undefined in account view.
    activeSleeve: (state): Sleeve | undefined => {
      return findScopeSleeve(state)
    },
    proposalName: (state): string => state.state.proposalName,
    sleeveCount: (state): number => state.state.sleeves.length,
    irrelevantRiskFactors: (state): ConfigKey[] => {
      const activeSleeve = findScopeSleeve(state)
      if (!activeSleeve) return []
      const assetClassRiskFactor =
        activeSleeve.state.riskFactors[ConfigKey.Type]
      if (!assetClassRiskFactor) return []
      return computeDisabledRiskFactors(assetClassRiskFactor)
    },

    investorInfo: (state): InvestorInfo => {
      const investor = state.state.accountBefore?.investor
      if (!investor) return DEFAULT_INVESTOR_INFO
      // Total investment must sum every entry under DoD multi-entry; reading
      // cash[0] would silently shrink the displayed total to the first
      // sleeve's share, and the next save would rescale the rest of the
      // array down to match (corruption hazard).
      const accountCash = state.state.accountBefore?.cash
      const cashTotal = calculateAvailableAccountCash(accountCash)
      return {
        investor_name: investor.name,
        state_of_residence: investor.tax_state ?? '',
        currency: getFormattingCurrency(state),
        federal_income_tax:
          investor.marginal_ordinary_taxes?.federal_tax_rate ?? 0,
        state_income_tax: investor.marginal_ordinary_taxes?.state_tax_rate ?? 0,
        total_investment:
          accountCash && accountCash.length > 0
            ? cashTotal
            : DEFAULT_INVESTOR_INFO.total_investment,
        minimum_order_increment: DEFAULT_INVESTOR_INFO.minimum_order_increment,
        minimum_quantity: DEFAULT_INVESTOR_INFO.minimum_quantity,
        tax_profile: investor.tax_profile,
        custom_income_tax:
          investor.custom_income_tax ?? DEFAULT_INVESTOR_INFO.custom_income_tax,
        custom_capital_gains_tax:
          investor.custom_capital_gains_tax ??
          DEFAULT_INVESTOR_INFO.custom_capital_gains_tax,
      }
    },

    currencyCode: (state): string => {
      return getFormattingCurrency(state)
    },

    // Null in account view, which has no sleeve whose currency to edit.
    sleeveCurrency: (state): Currency | null =>
      getSleeveCurrency(findScopeSleeve(state)),

    isHydrated: (state): boolean => state.state.accountBefore !== null,

    currencySymbol: (state): string => {
      return CURRENCY_SYMBOLS[getFormattingCurrency(state)] ?? '$'
    },

    hasEverOptimized: (state): boolean => Boolean(state.state.optimizationId),

    mode: (state): ProposalMode | undefined => state.state.metadata.mode,

    constraintRelaxation: (state) => state.state.constraintRelaxation,

    complianceMap: (state): ComplianceMap =>
      getSleeveComplianceMap(
        state.state.sleeveCompliance,
        findScopeSleeve(state)?.sleeveId,
      ),

    aggregateValidations: (state): AggregateConstraintValidation[] =>
      getSleeveAggregateValidations(
        state.state.sleeveCompliance,
        findScopeSleeve(state)?.sleeveId,
      ),

    cashflowTargets: (state) => {
      const activeSleeve = findScopeSleeve(state)
      return activeSleeve ? activeSleeve.state.cashflowTargets : []
    },
    objectiveStrategy: (state): ObjectiveStrategy | undefined => {
      const activeSleeve = findScopeSleeve(state)
      return activeSleeve?.state.objectiveStrategy
    },

    cashBuffer: (state): number => {
      const activeSleeve = findScopeSleeve(state)
      return activeSleeve?.state.cashBuffer ?? 0
    },

    // Holdings selectors follow the active scope: account-wide in account
    // view, the scope sleeve's slice otherwise.
    holdingsBefore: (state): Holding[] => getHoldingsBefore(state),

    holdingsAfter: (state): Holding[] => getHoldingsAfter(state),

    // Currency entries represent sleeve cash, not positions; cash-only
    // proposals have no holdings.
    hasHoldings: (state): boolean =>
      getHoldingsBefore(state).some(isPosition) ||
      getHoldingsAfter(state).some(isPosition),

    hasHoldingsAfter: (state): boolean =>
      getHoldingsAfter(state).some(isPosition),

    // Account-wide post-trade positions, read from the account-id key so it
    // stays stable regardless of the active sleeve scope. Gates account-level
    // actions (PDF export) that shouldn't hide when a sleeve tab with no
    // positions is focused.
    hasAccountHoldingsAfter: (state): boolean =>
      (
        state.state.holdingsAfter[state.state.activeAccount] ?? EMPTY_HOLDINGS
      ).some(isPosition),

    riskFactorsList: (state) => {
      const activeSleeve = findScopeSleeve(state)
      return activeSleeve ? Object.values(activeSleeve.state.riskFactors) : []
    },

    otherConstraints: (state) => {
      const activeSleeve = findScopeSleeve(state)
      return activeSleeve ? activeSleeve.state.otherConstraints : []
    },

    cashflowTargetPriority: (state): Priority =>
      findScopeSleeve(state)?.state.cashflowTargetPriority ??
      DEFAULT_CASHFLOW_TARGET_PRIORITY,

    cashflowBand: (state): CashflowBandConstraint | undefined => {
      const activeSleeve = findScopeSleeve(state)
      return activeSleeve
        ? readCashflowBand(activeSleeve.state.otherConstraints)
        : undefined
    },

    ladderPolicy: (state) => {
      const activeSleeve = findScopeSleeve(state)
      return activeSleeve ? activeSleeve.state.ladderPolicy : undefined
    },

    assignmentDate: (state): string | undefined => {
      const activeSleeve = findScopeSleeve(state)
      return activeSleeve?.state.assignmentDate
    },

    objectiveTokens: (state): EquationToken[] => {
      const activeSleeve = findScopeSleeve(state)
      return activeSleeve ? activeSleeve.state.objectiveTokens : []
    },

    // BANNER STATISTICS
    statistics: (state): SleeveStatistics | null => {
      if (state.activeScope === state.state.activeAccount) {
        return state.state.accountStatistics
      }
      return state.state.sleeveStatistics?.[state.activeScope] ?? null
    },
    allAllocationsLocked: (state): boolean => {
      const allocations = getScopedOrdersAndAllocations(state).flatMap(
        (order) => order.allocations,
      )
      return allocations.length > 0 && allocations.every((a) => a.lock)
    },
    hasSleeveAllocations: (state): boolean =>
      getScopedOrdersAndAllocations(state).some(
        (order) => order.allocations.length > 0,
      ),
    tradesCount: (state): number => getScopedOrdersAndAllocations(state).length,
    sleeveMarketValue: (state): number => getSleeveMarketValue(state),
    sleeveCash: (state): number => getScopedCash(state),
    sleeveStartingCash: (state): number => getScopedStartingCash(state),

    totalCashAvailable: (state): number => {
      const total =
        calculateAvailableAccountCash(state.state.accountBefore?.cash) +
        getSellOrdersProceeds(state)
      return Number(total.toFixed(2))
    },
  },
)
